import { IconArrowLeft } from "@/assets/icons/geist/IconArrowLeft";
import { ArchivedEnvironmentDeleteModal } from "@/components/archive/ArchivedEnvironmentDeleteModal";
import { ArchivedEnvironmentSearchInput } from "@/components/archive/ArchivedEnvironmentSearchInput";
import { ArchivedEnvironmentsList } from "@/components/archive/ArchivedEnvironmentsList";
import { AutoDeleteSelect } from "@/components/archive/AutoDeleteSelect";
import { Button } from "@/components/flexkit/Button";
import { Link } from "@/components/flexkit/Link";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Tooltip } from "@/components/Tooltip";
import { useEnvironmentsByProjects } from "@/hooks/use-environments-by-projects";
import { useEscapeShortcut } from "@/hooks/use-escape-shortcut";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useGroupedAgentExecutions } from "@/queries/agent-queries";
import { useOrganization } from "@/queries/organization-queries";
import { getProjectQuery } from "@/queries/project-queries";
import { mostRecentAgentExecution } from "@/utils/agent-executions";
import {
    type ArchivedEnvironment,
    environmentsByProjectsToArchivedEnvironments,
    getEnvironmentDisplayName,
    getProjectDisplayName,
} from "@/utils/environment";
import { useQueries } from "@tanstack/react-query";
import Fuse from "fuse.js";
import { ListEnvironmentsRequest_ArchivalStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { type FC, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

type ArchivedSidebarProps = {
    onArchiveViewChange: (isArchive: boolean) => void;
};

export const ArchivedSidebarContent: FC<ArchivedSidebarProps> = ({ onArchiveViewChange }) => {
    const { data: environmentsByProjects, isLoading: isLoading } = useEnvironmentsByProjects({
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ARCHIVED,
        includeAgentCreatedEnvironments: true,
    });

    const { data: groupedAgentExecutions } = useGroupedAgentExecutions();

    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue] = useDebounce(searchValue, 100, {});

    const archivedEnvironments = useMemo(
        () => (environmentsByProjects ? environmentsByProjectsToArchivedEnvironments(environmentsByProjects) : []),
        [environmentsByProjects],
    );
    const api = useGitpodAPI();

    const projectIds = useMemo(
        () => archivedEnvironments.map((env) => env.projectId).filter((id) => id !== undefined),
        [archivedEnvironments],
    );
    const projectQueries = useQueries({
        queries: projectIds.map((projectId) => getProjectQuery(api, projectId)),
    });

    // Filter environments based on search
    const filteredArchivedEnvironments = useMemo(() => {
        if (!debouncedSearchValue) {
            return archivedEnvironments;
        }

        // Add computed fields for search that match main sidebar naming
        const environmentsWithSearchFields = archivedEnvironments.map((env) => {
            const agentExecutions = groupedAgentExecutions?.environmentScoped.get(env.environment.id) ?? [];
            const agentExecution = mostRecentAgentExecution(agentExecutions);

            const projectQuery = projectQueries.find((q) => q.data?.id === env.projectId);
            const projectMissing = !!env.projectId && projectQuery?.isError;

            return {
                ...env,
                computedEnvironmentName: getEnvironmentDisplayName(env.environment, agentExecution),
                computedProjectName: getProjectDisplayName(projectQuery?.data, projectMissing),
            };
        });

        const fuse = new Fuse(environmentsWithSearchFields, {
            includeScore: true,
            threshold: 0.3,
            distance: 100,
            minMatchCharLength: 2,
            keys: [
                "computedEnvironmentName",
                "computedProjectName",
                "environment.status.content.git.branch",
                "environment.metadata.name",
            ],
        });
        const result = fuse.search(debouncedSearchValue);

        return result.map((r) => r.item);
    }, [archivedEnvironments, debouncedSearchValue, groupedAgentExecutions?.environmentScoped, projectQueries]);

    const [environmentsToDelete, setEnvironmentsToDelete] = useState<ArchivedEnvironment[]>([]);

    const handleDeleteAll = () => {
        setEnvironmentsToDelete(filteredArchivedEnvironments);
    };

    const handleDeleteSingle = (environment: ArchivedEnvironment) => {
        setEnvironmentsToDelete([environment]);
    };

    useEscapeShortcut(() => {
        onArchiveViewChange(false);
    });

    return (
        <div className="flex size-full flex-col">
            <div className="px-2 pt-2.5">
                <SidebarHeader
                    onArchiveViewChange={onArchiveViewChange}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    hasArchivedEnvironments={archivedEnvironments.length > 0}
                />
            </div>
            <ScrollArea orientation="vertical" className="mr-0.5 flex-grow pt-2">
                <ArchivedEnvironmentsList
                    archivedEnvironments={filteredArchivedEnvironments}
                    isLoading={isLoading}
                    onDelete={handleDeleteSingle}
                    search={debouncedSearchValue}
                />
            </ScrollArea>
            <div className="pb-2">
                <SidebarFooter
                    archivedEnvironments={filteredArchivedEnvironments}
                    isLoading={isLoading}
                    onDeleteAll={handleDeleteAll}
                />
            </div>
            {environmentsToDelete.length > 0 && (
                <ArchivedEnvironmentDeleteModal
                    onClose={() => setEnvironmentsToDelete([])}
                    archivedEnvironments={environmentsToDelete}
                />
            )}
        </div>
    );
};

type SidebarHeaderProps = {
    onArchiveViewChange: (isArchive: boolean) => void;
    searchValue: string;
    setSearchValue: (value: string) => void;
    hasArchivedEnvironments?: boolean;
};

const SidebarHeader: FC<SidebarHeaderProps> = ({
    onArchiveViewChange,
    searchValue,
    setSearchValue,
    hasArchivedEnvironments,
}) => {
    const { data: organization } = useOrganization();

    // Hide AutoDeleteSelect for FREE_ONA and CORE tiers
    const shouldShowAutoDelete = useMemo(() => {
        const tier = organization?.tier;
        return tier !== OrganizationTier.FREE_ONA && tier !== OrganizationTier.CORE;
    }, [organization?.tier]);

    return (
        <div className="flex flex-col">
            <div className="flex flex-col pb-4">
                <Tooltip
                    content={
                        <div className="flex flex-row items-center gap-2">
                            Archived environments
                            <KeyboardCombo keys={["Esc"]} />
                        </div>
                    }
                >
                    <Link
                        to="#"
                        className="flex h-8 flex-row items-center gap-1 rounded-lg pl-2 hover:bg-surface-03"
                        onClick={() => onArchiveViewChange(false)}
                        reloadIfFrontendUpdated={true}
                        data-tracking-id="archived-environments-sidebar"
                    >
                        <div className="w-5">
                            <IconArrowLeft size="base" />
                        </div>

                        <div className={cn("flex-grow truncate text-start text-base font-bold")}>
                            Archived environments
                        </div>
                    </Link>
                </Tooltip>
            </div>
            <div className="flex flex-col gap-2">
                <ArchivedEnvironmentSearchInput
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    disabled={!hasArchivedEnvironments}
                />
                {shouldShowAutoDelete && <AutoDeleteSelect />}
            </div>
        </div>
    );
};

type SidebarFooterProps = {
    archivedEnvironments: ArchivedEnvironment[];
    isLoading?: boolean;
    onDeleteAll: () => void;
};

const SidebarFooter: FC<SidebarFooterProps> = ({ archivedEnvironments, isLoading, onDeleteAll }) => {
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);

    useEffect(() => {
        if (archivedEnvironments.length > 0 && !isButtonEnabled) {
            const timer = setTimeout(() => {
                setIsButtonEnabled(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [archivedEnvironments.length, isButtonEnabled]);

    return (
        <div>
            {isLoading ? (
                <div className="mt-4 flex flex-col gap-[4px] pl-2 pr-4">
                    <SkeletonBlock className="h-8 w-full" ready={false} />
                </div>
            ) : (
                archivedEnvironments.length > 0 && (
                    <div className="mt-4 flex flex-col gap-[4px] pl-2 pr-4">
                        <Button
                            variant="destructive"
                            className="w-full"
                            onClick={onDeleteAll}
                            disabled={!isButtonEnabled}
                            data-tracking-id="delete-all-archived-environments-sidebar"
                        >
                            Delete All
                        </Button>
                    </div>
                )
            )}
        </div>
    );
};
